<template>
  <div>
    <Navigation />
    <div class="wrap">
      <div class="head">
        <div>
          <h1>Products</h1>
          <p class="lede">{{ products.length }} product{{ products.length === 1 ? '' : 's' }} in catalogue.</p>
        </div>
        <div class="head-actions">
          <input v-model="search" placeholder="Search by name / SKU / barcode…" style="width:280px" @keyup.enter="load" />
          <button class="secondary" @click="load">Search</button>
          <button class="secondary" @click="openImport" v-if="can('products.create')"><i class="mdi mdi-upload"></i> Bulk import</button>
          <button class="primary" @click="openNew" v-if="can('products.create')">+ New Product</button>
        </div>
      </div>

      <div class="card">
        <table v-if="products.length">
          <thead>
            <tr>
              <th style="width:56px"></th><th>Name</th><th>Category</th><th>Brand</th><th>Variants</th><th>Base Price</th><th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="p in products" :key="p.id" @click="openEdit(p)" class="clickable">
              <td>
                <div class="thumb">
                  <img v-if="p.image_url" :src="photoSrc(p.image_url)" alt="" />
                  <i v-else class="mdi mdi-image-outline"></i>
                </div>
              </td>
              <td>{{ p.name }}</td>
              <td>{{ p.category_name || '—' }}</td>
              <td>{{ p.brand_name || '—' }}</td>
              <td>{{ p.variant_count }}</td>
              <td>KES {{ format(p.base_price) }}</td>
              <td><button class="secondary small">Edit</button></td>
            </tr>
          </tbody>
        </table>
        <p v-else class="muted center">No products yet. Click <strong>+ New Product</strong> to add your first item.</p>
      </div>
    </div>

    <!-- Bulk import modal -->
    <div v-if="showImport" class="modal-back" @click.self="showImport=false">
      <div class="modal card wide">
        <h3>Bulk import products (CSV)</h3>
        <p class="muted small">
          CSV columns (in order): <code>name, category, brand, sku, barcode, size, color, cost, sell, stock, item_type</code>.
          <br />Only <strong>name</strong> and <strong>sell</strong> are required. Categories and brands are created if they don't exist.
          <a href="javascript:void(0)" @click="downloadTemplate">Download blank template</a>
        </p>
        <input type="file" accept=".csv,text/csv" @change="onCsv" />
        <p v-if="importRows.length" class="muted small" style="margin-top:8px;">
          Parsed <strong>{{ importRows.length }}</strong> row{{ importRows.length === 1 ? '' : 's' }}. Preview first 5:
        </p>
        <table v-if="importRows.length" class="preview">
          <thead><tr><th>Name</th><th>SKU</th><th>Cost</th><th>Sell</th><th>Stock</th></tr></thead>
          <tbody>
            <tr v-for="(r, i) in importRows.slice(0, 5)" :key="i">
              <td>{{ r.name }}</td><td class="muted small">{{ r.sku || '(auto)' }}</td>
              <td>{{ r.cost }}</td><td>{{ r.sell }}</td><td>{{ r.stock }}</td>
            </tr>
          </tbody>
        </table>
        <div v-if="importResult" class="import-result">
          <strong>{{ importResult.dryRun ? 'Dry run:' : 'Imported:' }}</strong>
          Created {{ importResult.created }} · Updated {{ importResult.updated }} · Skipped {{ importResult.skipped }}
          <div v-if="importResult.errors && importResult.errors.length" class="err-list">
            <div v-for="(e, i) in importResult.errors" :key="i" class="small warn">{{ e }}</div>
          </div>
        </div>
        <div class="modal-actions">
          <button class="secondary" @click="showImport=false">Close</button>
          <button class="secondary" @click="doImport(true)" :disabled="!importRows.length || importing">Dry run</button>
          <button class="primary" @click="doImport(false)" :disabled="!importRows.length || importing">
            {{ importing ? 'Importing…' : 'Import ' + importRows.length + ' row(s)' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal -->
    <div v-if="showModal" class="modal-back" @click.self="showModal=false">
      <div class="modal card">
        <h3>{{ editingId ? 'Edit Product' : 'New Product' }}</h3>
        <div class="photo-row" v-if="editingId">
          <div class="photo-box">
            <img v-if="form.imageUrl" :src="photoSrc(form.imageUrl)" alt="" />
            <div v-else class="photo-placeholder"><i class="mdi mdi-image-outline"></i></div>
          </div>
          <div class="photo-actions">
            <input type="file" ref="photoInput" accept="image/*" style="display:none" @change="onPhoto" />
            <button type="button" class="secondary" @click="$refs.photoInput.click()" :disabled="uploadingPhoto">
              <i class="mdi mdi-camera"></i> {{ uploadingPhoto ? 'Uploading…' : (form.imageUrl ? 'Change photo' : 'Add photo') }}
            </button>
            <p class="muted small">JPEG / PNG / WebP · max 3 MB</p>
          </div>
        </div>
        <p v-else-if="!editingId" class="muted small">Save the product first, then you can upload a photo.</p>

        <label>Product name</label>
        <input v-model="form.name" placeholder="e.g. Nike Air Force 1" />

        <div class="row-2">
          <div>
            <label>Item type</label>
            <select v-model="form.itemType" @change="onItemTypeChange">
              <option value="retail">Retail (physical stock)</option>
              <option value="menu_item">Menu item (restaurant / bar / café)</option>
              <option value="service">Service (no stock)</option>
            </select>
          </div>
          <div>
            <label>Base price (KES)</label>
            <input type="number" v-model.number="form.basePrice" />
          </div>
        </div>

        <div class="row-2" v-if="form.itemType === 'menu_item'">
          <div>
            <label>Kitchen station</label>
            <select v-model.number="form.stationId">
              <option :value="0">— none (bar / no-cook) —</option>
              <option v-for="s in stations" :key="s.id" :value="s.id">{{ s.name }} ({{ s.type }})</option>
            </select>
          </div>
          <div>
            <label>Prep time (min)</label>
            <input type="number" v-model.number="form.prepTimeMinutes" />
          </div>
        </div>

        <div class="row-2" v-if="form.itemType !== 'menu_item'">
          <div>
            <label>Tax rate (%)</label>
            <input type="number" v-model.number="form.taxRate" />
          </div>
        </div>

        <label v-if="form.itemType === 'retail'">
          <input type="checkbox" v-model="hasVariants" style="width:auto; margin-right:6px" />
          This product has size / colour variants
        </label>

        <div v-if="!hasVariants">
          <label>SKU / Barcode</label>
          <input v-model="singleVariant.sku" placeholder="Optional — auto-generated if blank" />
          <div class="row-2">
            <div>
              <label>Cost price</label>
              <input type="number" v-model.number="singleVariant.cost_price" />
            </div>
            <div>
              <label>Opening stock</label>
              <input type="number" v-model.number="singleVariant.initial_stock" />
            </div>
          </div>
        </div>

        <div v-else>
          <label>Variants</label>
          <table class="variants-mini">
            <thead><tr><th>Size</th><th>Colour</th><th>SKU</th><th>Cost</th><th>Sell</th><th>Stock</th><th></th></tr></thead>
            <tbody>
              <tr v-for="(v, i) in form.variants" :key="i">
                <td><input v-model="v.size" style="width:60px" /></td>
                <td><input v-model="v.color" style="width:80px" /></td>
                <td><input v-model="v.sku" style="width:90px" placeholder="auto" /></td>
                <td><input type="number" v-model.number="v.cost_price" style="width:70px" /></td>
                <td><input type="number" v-model.number="v.sell_price" style="width:70px" /></td>
                <td><input type="number" v-model.number="v.initial_stock" style="width:60px" /></td>
                <td><button class="danger small" @click="form.variants.splice(i,1)">&times;</button></td>
              </tr>
            </tbody>
          </table>
          <button class="secondary small" @click="addVariant">+ Add variant</button>
        </div>

        <div v-if="form.itemType === 'menu_item'" class="recipe-section">
          <label>Recipe (ingredients consumed when this item is sold)</label>
          <div class="ing-search">
            <input v-model="ingSearch" @input="onIngSearch" placeholder="Search a stocked ingredient variant…" />
            <div v-if="ingResults.length" class="ing-results">
              <div v-for="v in ingResults" :key="v.id" class="ing-result" @click="addIngredient(v)">
                <strong>{{ v.product_name }}</strong> · {{ v.variant_name }}
                <span class="muted small">· {{ v.sku }}</span>
              </div>
            </div>
          </div>
          <table v-if="form.recipeItems.length" class="ing-table">
            <thead><tr><th>Ingredient</th><th style="width:100px">Qty per sale</th><th>Note</th><th></th></tr></thead>
            <tbody>
              <tr v-for="(r, i) in form.recipeItems" :key="i">
                <td>{{ r.name }}</td>
                <td><input type="number" step="0.01" min="0" v-model.number="r.quantity" style="width:80px; padding:4px" /></td>
                <td><input v-model="r.note" placeholder="e.g. bun, patty" style="width:100%; padding:4px" /></td>
                <td><button class="danger small" @click="form.recipeItems.splice(i,1)">&times;</button></td>
              </tr>
            </tbody>
          </table>
          <p v-else class="muted small">No recipe items yet. Add ingredients so stock decreases when this item sells.</p>
        </div>

        <div class="modal-actions">
          <button class="secondary" @click="showModal=false">Cancel</button>
          <button class="primary" @click="save" :disabled="saving">{{ saving ? 'Saving…' : 'Save' }}</button>
        </div>
        <p class="err" v-if="error">{{ error }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Navigation from '@/components/paysol-nav/Navigation.vue';
import { paysol, execute, can, base } from '@/api';

export default {
  name: 'ProductManagement',
  components: { Navigation },
  data() {
    return {
      products: [], search: '',
      showModal: false, saving: false, error: '',
      editingId: 0,
      form: { name: '', basePrice: 0, taxRate: 0, variants: [], itemType: 'retail', stationId: 0, prepTimeMinutes: 0, recipeItems: [], imageUrl: '' },
      uploadingPhoto: false,
      hasVariants: true,
      singleVariant: { sku: '', cost_price: 0, initial_stock: 0 },
      stations: [],
      ingSearch: '', ingResults: [],
      // Bulk import
      showImport: false, importing: false,
      importRows: [], importResult: null,
    };
  },
  async mounted() { await Promise.all([this.load(), this.loadStations()]); },
  methods: {
    can(k) { return can(k); },
    format(n) { return Number(n || 0).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); },
    async load() {
      const { data } = await execute({ function: 'listProducts', search: this.search }, paysol);
      if (data.success) this.products = data.data;
    },
    async loadStations() {
      try {
        const { data } = await execute({ function: 'listStations' }, paysol);
        if (data.success) this.stations = data.data;
      } catch (e) { /* stations only relevant for restaurant tenants */ }
    },
    openNew() {
      this.editingId = 0;
      this.form = { name: '', basePrice: 0, taxRate: 0, variants: [], itemType: 'retail', stationId: 0, prepTimeMinutes: 0, recipeItems: [], imageUrl: '' };
      this.singleVariant = { sku: '', cost_price: 0, initial_stock: 0 };
      this.hasVariants = true; this.error = ''; this.showModal = true;
      this.ingSearch = ''; this.ingResults = [];
      this.addVariant();
    },
    async openEdit(p) {
      const { data } = await execute({ function: 'getProduct', id: p.id }, paysol);
      if (!data.success) return;
      this.editingId = p.id;
      this.form = {
        name: data.data.name,
        basePrice: parseFloat(data.data.base_price),
        taxRate: parseFloat(data.data.tax_rate),
        variants: [],
        itemType: data.data.item_type || 'retail',
        stationId: data.data.station_id ? Number(data.data.station_id) : 0,
        prepTimeMinutes: data.data.prep_time_minutes ? Number(data.data.prep_time_minutes) : 0,
        imageUrl: data.data.image_url || '',
        recipeItems: (data.data.recipe || []).map(r => ({
          ingredientVariantId: r.ingredient_variant_id,
          quantity: Number(r.quantity),
          note: r.note || '',
          name: r.ingredient_product_name + ' · ' + r.ingredient_variant_name,
        })),
      };
      this.hasVariants = data.data.has_variants === 1;
      this.error = ''; this.ingSearch = ''; this.ingResults = []; this.showModal = true;
    },
    onItemTypeChange() {
      // If switching to menu_item and no variants defined yet, add a stub single variant
      if (this.form.itemType === 'menu_item' && this.form.variants.length === 0) {
        this.form.variants.push({ size: '', color: '', sku: '', cost_price: 0, sell_price: this.form.basePrice, initial_stock: 0 });
      }
    },
    onIngSearch() {
      const q = this.ingSearch.trim();
      if (q.length < 2) { this.ingResults = []; return; }
      clearTimeout(this._ingTimer);
      this._ingTimer = setTimeout(async () => {
        const { data } = await execute({ function: 'listProducts', search: q }, paysol);
        if (!data.success) return;
        const out = [];
        for (const p of data.data.slice(0, 5)) {
          // Skip menu items as ingredients
          if ((p.item_type || 'retail') === 'menu_item') continue;
          const { data: pd } = await execute({ function: 'getProduct', id: p.id }, paysol);
          if (pd.success) for (const v of pd.data.variants || []) {
            out.push({ id: v.id, product_name: p.name, variant_name: v.variant_name, sku: v.sku });
            if (out.length >= 12) break;
          }
          if (out.length >= 12) break;
        }
        this.ingResults = out;
      }, 300);
    },
    addIngredient(v) {
      if (this.form.recipeItems.some(r => r.ingredientVariantId === v.id)) return;
      this.form.recipeItems.push({
        ingredientVariantId: v.id, quantity: 1, note: '',
        name: v.product_name + ' · ' + v.variant_name,
      });
      this.ingSearch = ''; this.ingResults = [];
    },
    addVariant() {
      this.form.variants.push({ size: '', color: '', sku: '', cost_price: 0, sell_price: this.form.basePrice, initial_stock: 0 });
    },
    // ---- Bulk import ----
    openImport() { this.showImport = true; this.importRows = []; this.importResult = null; },
    downloadTemplate() {
      const csv = 'name,category,brand,sku,barcode,size,color,cost,sell,stock,item_type\n' +
                  'Nike Air Force 1,Sneakers,Nike,AF1-42-BLK,,42,Black,4200,8500,10,retail\n' +
                  'Nike Air Force 1,Sneakers,Nike,AF1-40-WHT,,40,White,4200,8500,8,retail\n';
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'paysol-products-template.csv';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    onCsv(e) {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => this.parseCsv(ev.target.result);
      reader.readAsText(file);
    },
    parseCsv(text) {
      // Minimal CSV parser: handles quoted fields
      const rows = [];
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (!lines.length) { this.importRows = []; return; }
      const headers = this.splitCsvLine(lines[0]).map(h => h.trim().toLowerCase());
      for (let i = 1; i < lines.length; i++) {
        const cells = this.splitCsvLine(lines[i]);
        const row = {};
        headers.forEach((h, idx) => { row[h] = cells[idx] !== undefined ? cells[idx] : ''; });
        rows.push(row);
      }
      this.importRows = rows; this.importResult = null;
    },
    splitCsvLine(line) {
      const out = []; let cur = ''; let inQuote = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"' && line[i+1] === '"') { cur += '"'; i++; }
        else if (ch === '"') inQuote = !inQuote;
        else if (ch === ',' && !inQuote) { out.push(cur); cur = ''; }
        else cur += ch;
      }
      out.push(cur);
      return out;
    },
    photoSrc(u) { return u.startsWith('http') ? u : (base + u); },
    async onPhoto(e) {
      const file = e.target.files[0];
      if (!file || !this.editingId) return;
      this.uploadingPhoto = true;
      try {
        const fd = new FormData();
        fd.append('function', 'uploadProductPhoto');
        fd.append('productId', this.editingId);
        fd.append('photo', file);
        const token = localStorage.getItem('authToken');
        const { data } = await axios.post(paysol, fd, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!data.success) { alert(data.message); return; }
        this.form.imageUrl = data.data.imageUrl + '?t=' + Date.now();
        await this.load();
      } finally { this.uploadingPhoto = false; if (this.$refs.photoInput) this.$refs.photoInput.value = ''; }
    },
    async doImport(dryRun) {
      this.importing = true; this.importResult = null;
      try {
        const { data } = await execute({
          function: 'bulkImportProducts', dryRun: dryRun ? 1 : 0, rows: this.importRows
        }, paysol);
        if (!data.success) { alert(data.message); return; }
        this.importResult = data.data;
        if (!dryRun && data.data.created + data.data.updated > 0) await this.load();
      } finally { this.importing = false; }
    },
    async save() {
      this.error = ''; this.saving = true;
      try {
        const payload = {
          function: 'saveProduct',
          id: this.editingId,
          name: this.form.name,
          basePrice: this.form.basePrice,
          taxRate: this.form.taxRate,
          hasVariants: this.form.itemType === 'menu_item' ? 0 : (this.hasVariants ? 1 : 0),
          isService: this.form.itemType === 'service' ? 1 : 0,
          itemType: this.form.itemType,
          stationId: this.form.stationId || 0,
          prepTimeMinutes: this.form.prepTimeMinutes || 0,
          variants: this.form.itemType === 'menu_item'
            ? [{ sku: '', cost_price: 0, initial_stock: 0, sell_price: this.form.basePrice }]
            : (this.hasVariants ? this.form.variants : [{ ...this.singleVariant, sell_price: this.form.basePrice }]),
          recipeItems: this.form.itemType === 'menu_item' ? this.form.recipeItems.map(r => ({
            ingredientVariantId: r.ingredientVariantId, quantity: r.quantity, note: r.note,
          })) : [],
        };
        const { data } = await execute(payload, paysol);
        if (!data.success) { this.error = data.message; return; }
        this.showModal = false;
        await this.load();
      } catch (e) {
        this.error = e.response?.data?.message || 'Save failed.';
      } finally { this.saving = false; }
    }
  }
};
</script>

<style scoped>
.wrap { max-width: 1200px; margin: 0 auto; padding: 24px; }
.head { display: flex; justify-content: space-between; align-items: end; margin-bottom: 20px; gap: 20px; }
h1 { font-size: 26px; margin: 0 0 4px; }
.lede { color: var(--paysol-muted); margin: 0; }
.head-actions { display: flex; gap: 8px; align-items: center; }
.clickable { cursor: pointer; }
.muted { color: var(--paysol-muted); }
.center { text-align: center; padding: 40px 0; }
button.small { padding: 4px 10px; font-size: 11px; }
.modal-back { position: fixed; inset: 0; background: rgba(0,0,0,0.6); display: flex; align-items: center; justify-content: center; z-index: 50; padding: 24px; }
.modal { width: 100%; max-width: 620px; max-height: 90vh; overflow-y: auto; }
.row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.variants-mini { margin: 8px 0; }
.variants-mini input { padding: 4px 6px; font-size: 12px; }
.modal-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 20px; padding-top: 14px; border-top: 1px solid var(--paysol-line); }
.err { color: var(--paysol-danger); font-size: 12px; margin-top: 8px; }
.recipe-section { margin-top: 12px; padding-top: 12px; border-top: 1px solid var(--paysol-line); }
.ing-search { position: relative; margin-top: 6px; }
.ing-results { position: absolute; z-index: 5; background: var(--paysol-card); border: 1px solid var(--paysol-line); border-radius: 6px; margin-top: 4px; max-height: 220px; overflow-y: auto; width: 100%; box-shadow: 0 8px 20px rgba(0,0,0,.15); }
.ing-result { padding: 8px 12px; cursor: pointer; border-bottom: 1px solid var(--paysol-line); font-size: 13px; }
.ing-result:hover { background: var(--paysol-teal-glow); }
.ing-table { margin: 8px 0; }
.modal.wide { max-width: 720px; }
.preview { margin: 10px 0; }
.import-result { padding: 12px; background: var(--paysol-bg-elev); border-radius: 6px; margin-top: 12px; font-size: 13px; }
.err-list { margin-top: 10px; max-height: 200px; overflow-y: auto; }
.warn { color: var(--paysol-warning); }
a { color: var(--paysol-teal); }
.thumb { width: 44px; height: 44px; border-radius: 6px; background: var(--paysol-bg-elev); border: 1px solid var(--paysol-line); overflow: hidden; display: flex; align-items: center; justify-content: center; }
.thumb img { width: 100%; height: 100%; object-fit: cover; }
.thumb i { color: var(--paysol-muted); font-size: 22px; }
.photo-row { display: flex; gap: 14px; align-items: center; padding: 12px 0 4px; }
.photo-box { width: 100px; height: 100px; border-radius: 8px; background: var(--paysol-bg-elev); border: 1px solid var(--paysol-line); overflow: hidden; display: flex; align-items: center; justify-content: center; }
.photo-box img { width: 100%; height: 100%; object-fit: cover; }
.photo-placeholder i { font-size: 40px; color: var(--paysol-muted); }
.photo-actions { display: flex; flex-direction: column; gap: 6px; }
</style>
